// OnboardingPage — First-time user walkthrough
// Multi-step: Welcome → Goals → Tier preview → Activate

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierPill } from '../../components/Brand';

const STEPS = [
  { key: 'welcome',  icon: '👋', title: 'Welcome to Funder Pro', sub: 'Your AI-powered path to funded trading' },
  { key: 'goals',    icon: '🎯', title: 'Set your goals',        sub: 'We\'ll tailor your experience' },
  { key: 'experience', icon: '📊', title: 'How experienced are you?', sub: 'Helps us pick the right tier' },
  { key: 'tier',     icon: '🏆', title: 'Pick a starting tier',   sub: 'You can change this anytime' },
] as const;
type StepKey = typeof STEPS[number]['key'];

const EXPERIENCE = [
  { key: 'beginner',     label: 'Brand new',         sub: 'Just getting started' },
  { key: 'some',         label: 'Some experience',   sub: 'Traded for 1-2 years' },
  { key: 'experienced',  label: 'Experienced',       sub: 'Traded for 3-5 years' },
  { key: 'pro',          label: 'Professional',      sub: '5+ years, full-time' },
];

const TIERS = [
  { key: 'Beginner',     name: 'Beginner',     account: '$50K',  fee: '$79',  emoji: '🌱', color: '#34C759' },
  { key: 'Mid',          name: 'Mid',          account: '$100K', fee: '$149', emoji: '🚀', color: '#007AFF' },
  { key: 'Professional', name: 'Professional', account: '$150K', fee: '$249', emoji: '💎', color: '#1C1C1E' },
];

export function OnboardingPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<StepKey>('welcome');
  const [goal, setGoal] = useState<string | null>(null);
  const [exp, setExp] = useState<string | null>(null);
  const [tier, setTier] = useState<string>('Beginner');
  const idx = STEPS.findIndex((s) => s.key === step);

  function next() { setStep(STEPS[Math.min(idx + 1, STEPS.length - 1)].key); }
  function back() { if (idx === 0) return; setStep(STEPS[idx - 1].key); }

  return (
    <div className="min-h-screen bg-tf-surface-1 flex flex-col">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        {idx > 0 ? (
          <button onClick={back} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        ) : <div className="w-9" />}
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Get started</div>
        <button onClick={() => nav('/portal/dashboard')} data-testid="skip" className="px-3 text-[15px] text-tf-text-3">Skip</button>
      </div>

      {/* Step dots */}
      <div className="px-4 pt-3 flex items-center justify-center gap-1.5" data-testid="onboarding-dots">
        {STEPS.map((s, i) => (
          <div
            key={s.key}
            className={['h-1 rounded-full transition-all', i === idx ? 'w-6 bg-tf-blue' : i < idx ? 'w-1.5 bg-tf-blue' : 'w-1.5 bg-[#E5E5EA]'].join(' ')}
            data-testid={`dot-${s.key}`}
          />
        ))}
      </div>

      <div className="flex-1 px-4 pt-8 pb-32">
        {step === 'welcome' && (
          <div className="text-center">
            <div className="mx-auto flex h-32 w-32 items-center justify-center rounded-[36px] text-[64px] bg-tf-blue" style={{ background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)', boxShadow: '0 20px 40px rgba(0,122,255,0.30)' }}>🚀</div>
            <h1 className="mt-6 text-[28px] font-bold tracking-tight text-tf-text-1">Welcome to Funder Pro</h1>
            <p className="mt-2 text-[15px] text-tf-text-3 max-w-xs mx-auto">Trade with our capital. Keep up to 90% of the profits. No risk to you.</p>
            <div className="mt-8 space-y-2 text-left">
              {[
                { icon: '💰', label: 'Get funded up to $150K' },
                { icon: '🤖', label: 'AI bot trades while you sleep' },
                { icon: '📈', label: 'Up to 90% profit split' },
                { icon: '⚡', label: 'Pass in 5-8 funded days' },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-3 rounded-[14px] border border-tf-border bg-white p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue-tint text-[18px]">{b.icon}</div>
                  <span className="text-[14px] font-medium text-tf-text-1">{b.label}</span>
                  <svg viewBox="0 0 24 24" className="ml-auto h-4 w-4 text-tf-profit" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 'goals' && (
          <div>
            <h2 className="text-[24px] font-bold tracking-tight text-tf-text-1">What's your goal?</h2>
            <p className="mt-1 text-[14px] text-tf-text-3 mb-4">Pick one or more. We'll match the right plan.</p>
            <div className="space-y-2">
              {[
                { key: 'income',    icon: '💵', label: 'Generate side income',       sub: '$1-5K/month' },
                { key: 'career',    icon: '💼', label: 'Build a trading career',     sub: 'Full-time income' },
                { key: 'learn',     icon: '🎓', label: 'Learn to trade risk-free',   sub: 'Practice with our capital' },
                { key: 'grow',      icon: '🌱', label: 'Grow my capital',            sub: 'Scale my account' },
              ].map((g) => (
                <button
                  key={g.key}
                  onClick={() => setGoal(g.key)}
                  data-testid={`goal-${g.key}`}
                  className={['flex w-full items-center gap-3 rounded-[14px] p-3 transition text-left',
                    goal === g.key ? 'bg-tf-text-1 text-white' : 'border border-tf-border bg-white text-tf-text-1 active:opacity-40'
                  ].join(' ')}
                >
                  <div className={['flex h-10 w-10 items-center justify-center rounded-[10px] text-[20px]', goal === g.key ? 'bg-white/15' : 'bg-tf-blue-tint'].join(' ')}>{g.icon}</div>
                  <div className="flex-1">
                    <div className="text-[14px] font-semibold">{g.label}</div>
                    <div className={['text-[11px]', goal === g.key ? 'text-white/70' : 'text-tf-text-3'].join(' ')}>{g.sub}</div>
                  </div>
                  {goal === g.key && <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'experience' && (
          <div>
            <h2 className="text-[24px] font-bold tracking-tight text-tf-text-1">How experienced are you?</h2>
            <p className="mt-1 text-[14px] text-tf-text-3 mb-4">This helps us recommend the best tier for you.</p>
            <div className="space-y-2">
              {EXPERIENCE.map((e) => (
                <button
                  key={e.key}
                  onClick={() => setExp(e.key)}
                  data-testid={`exp-${e.key}`}
                  className={['flex w-full items-center gap-3 rounded-[14px] p-3 transition text-left',
                    exp === e.key ? 'bg-tf-text-1 text-white' : 'border border-tf-border bg-white text-tf-text-1 active:opacity-40'
                  ].join(' ')}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-purple-tint text-tf-info text-[18px]">📊</div>
                  <div className="flex-1">
                    <div className="text-[14px] font-semibold">{e.label}</div>
                    <div className={['text-[11px]', exp === e.key ? 'text-white/70' : 'text-tf-text-3'].join(' ')}>{e.sub}</div>
                  </div>
                  {exp === e.key && <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'tier' && (
          <div>
            <h2 className="text-[24px] font-bold tracking-tight text-tf-text-1">Pick your starting tier</h2>
            <p className="mt-1 text-[14px] text-tf-text-3 mb-4">You can upgrade or change anytime. Most beginners start here.</p>
            <div className="space-y-2">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTier(t.key)}
                  data-testid={`tier-${t.key}`}
                  className={['flex w-full items-center gap-3 rounded-[14px] p-4 transition text-left',
                    tier === t.key ? 'border-2 bg-white' : 'border border-tf-border bg-white'
                  ].join(' ')}
                  style={tier === t.key ? { borderColor: t.color, backgroundColor: t.color + '08' } : {}}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-[14px] text-[28px]" style={{ backgroundColor: t.color + '18' }}>{t.emoji}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[15px] font-bold text-tf-text-1">{t.name}</span>
                      <TierPill tier={t.key as any} />
                    </div>
                    <div className="text-[11px] text-tf-text-3">Account: {t.account} · Activation: {t.fee}</div>
                  </div>
                  {tier === t.key && <svg viewBox="0 0 24 24" className="h-5 w-5" style={{ color: t.color }} fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>}
                </button>
              ))}
            </div>
            {tier === 'Professional' && (
              <p className="mt-3 text-center text-[11px] text-tf-warning">Pro tier requires additional verification</p>
            )}
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-10 bg-white/80 backdrop-blur-xl border-t border-tf-border px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <button
          onClick={() => {
            if (step === 'tier') {
              localStorage.setItem('fp_user', JSON.stringify({ full_name: 'Brandon', email: 'demo@funderpro.live', tier }));
              nav('/portal/dashboard');
            } else {
              next();
            }
          }}
          disabled={(step === 'goals' && !goal) || (step === 'experience' && !exp)}
          data-testid="onboarding-next"
          className="flex h-12 w-full items-center justify-center rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.98] disabled:opacity-40"
        >
          {step === 'tier' ? `Start with ${tier}` : 'Continue'}
        </button>
      </div>
    </div>
  );
}
